import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconClose } from '../../../assets/icons/IconClose';
import { Button } from '../../../ui/Button/Button';
import { RelanceOnboardingConsent } from '../../../components/RelanceOnboardingConsent/RelanceOnboardingConsent';
import { onboardingService } from '../../../services/onboardingService';
import { useOnboardingReactivationStore } from '../../../stores/onboardingReactivationStore';
import { useOnboardingStore } from '../../../stores/onboardingStore';
import { RelanceOnboardingStatus } from '../../../types/onboarding';
import { useFullSyncOnHome } from '../../../hooks/useFullSyncOnHome';
import { TranslationKey } from '../../../i18n/translationKeys';

/**
 * Lets a user who previously refused relance onboarding turn it back on. Re-captures explicit Loi 25
 * consent (the same consent block as the walkthrough's screen 3, minus the refusal link) before
 * reactivating and triggering a sync — the walkthrough's screens 1-2 are not repeated, only the
 * consent step is. The confirmation lives in a modal (matching ReactivateRelanceModal's
 * backdrop/panel/escape-key conventions) rather than an
 * inline expanding row, so the consent block always gets the full dialog width instead of being
 * squeezed beside the settings row's action button.
 *
 * Renders nothing unless the company's decision is Refused and this user is an administrator: the
 * decision is the company's, so only an administrator can take it back — see IRelanceOnboardingService.
 */
export const OnboardingReactivationPanel: React.FC = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState<RelanceOnboardingStatus | null>(null);
    const [isAdministrator, setIsAdministrator] = useState(false);
    const [reactivating, setReactivating] = useState(false);
    const [consentChecked, setConsentChecked] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const syncOnHome = useFullSyncOnHome();
    const fetchStatus = useOnboardingReactivationStore(s => s.fetchStatus);

    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const { status: current, isAdministrator: administrator } = await fetchStatus();
                if (!cancelled) {
                    setStatus(current);
                    setIsAdministrator(administrator);
                }
            } catch (e) {
                // Leave status null — the panel renders nothing rather than guessing a wrong state.
                console.error('Failed to fetch onboarding status:', e);
            }
        })();
        return () => { cancelled = true; };
    }, [fetchStatus]);

    useEffect(() => {
        if (!reactivating) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setReactivating(false);
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [reactivating]);

    if (status !== RelanceOnboardingStatus.Refused || !isAdministrator) return null;

    const closeModal = () => {
        setReactivating(false);
        setConsentChecked(false);
    };

    const handleReactivate = async () => {
        setSubmitting(true);
        try {
            await onboardingService.reactivate();
            setStatus(RelanceOnboardingStatus.Completed);
            useOnboardingStore.getState().setStatus(RelanceOnboardingStatus.Completed);
            setReactivating(false);
            syncOnHome();
        } catch (e) {
            // Leave the modal open on failure so the confirm button stays available to retry.
            console.error('Failed to reactivate onboarding:', e);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <hr className="border-primary-200" />
            <div className="flex items-start justify-between gap-l">
                <div className="flex-1">
                    <h2 className="text-block-title text-content">{t(TranslationKey.Onboarding.Reactivation.Title)}</h2>
                    <p className="text-primary-700">{t(TranslationKey.Onboarding.Reactivation.Description)}</p>
                </div>

                <Button
                    data-testid="settings-onboarding-reactivate-btn"
                    variant="secondary"
                    onClick={() => setReactivating(true)}
                >
                    {t(TranslationKey.Onboarding.Reactivation.ReactivateButton)}
                </Button>
            </div>

            {reactivating && (
                <div
                    data-testid="onboarding-reactivate-modal"
                    className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center"
                    onClick={closeModal}
                    role="dialog"
                    aria-modal="true"
                >
                    <div
                        className="bg-primary-50 rounded-lg shadow-strong w-full max-w-md mx-l p-xl flex flex-col gap-l"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between">
                            <h2 className="text-page-title text-content">
                                {t(TranslationKey.Onboarding.Reactivation.ModalHeading)}
                            </h2>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="p-1.5 shrink-0 text-primary-700 hover:text-primary-1200 hover:bg-primary-200 rounded-full cursor-pointer transition-colors"
                                aria-label={t(TranslationKey.Onboarding.Reactivation.CancelButton)}
                            >
                                <IconClose className="w-5 h-5" />
                            </button>
                        </div>

                        <RelanceOnboardingConsent checked={consentChecked} onCheckedChange={setConsentChecked} />

                        <div className="flex justify-end items-center gap-m">
                            <Button
                                variant="secondary"
                                type="button"
                                onClick={closeModal}
                                data-testid="onboarding-reactivate-cancel-btn"
                            >
                                {t(TranslationKey.Onboarding.Reactivation.CancelButton)}
                            </Button>
                            <Button
                                data-testid="settings-onboarding-reactivate-confirm-btn"
                                onClick={() => void handleReactivate()}
                                disabled={!consentChecked}
                                loading={submitting}
                            >
                                {t(TranslationKey.Onboarding.Reactivation.ConfirmButton)}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};
